import { trackEvent } from '@99designs/common/utils/platform';
import Cookies from 'cookies-js';
import { AppState } from 'interactive/hooks/useAppState';
import industries from 'interactive/industries';
import logoTypes, { LogoType, LogoTypeKey } from 'interactive/logo-types';
import BundleSelect from 'interactive/steps/BundleSelect';
import IndustrySelect from 'interactive/steps/IndustrySelect';
import IntentSelect from 'interactive/steps/IntentSelect';
import LogoTypesSelect from 'interactive/steps/LogoTypesSelect';
import NameInput from 'interactive/steps/NameInput';
import Title from 'interactive/Title';
import React from 'react';
import {
    BundleContent,
    BundleOption,
    BundleOptionKey,
    BundlePrice,
    bundlesContent,
} from './bundle-options';

interface TitleProps {
    step: number;
}

interface StepProps {
    step: number;
    categoryKey: string;
    logoDesignBundlePrices: BundlePrice[];
    currentState: AppState;
    onSave(state: AppState): AppState;
    hasAnimation: boolean;
    resetAnimation: () => void;
}

const COMPLETED_STEP_EVENT = 'Completed Prelaunch Interactive Step';

export const defaultConfig = {
    maxSteps: Cookies.get('99x_labs') === 'enabled' ? 5 : 4, // TODO: remove cookie condition and set to 5 when bundles are ready
    TitleComponent: ({ step }: TitleProps) => {
        switch (step) {
            case 1:
                return (
                    <Title
                        title={__('Let’s get started with logo design…')}
                        subTitle={__('How can we help you?')}
                    />
                );
            case 2:
                return (
                    <Title
                        title={__('Great, tell us a little more about your business…')}
                        subTitle={__('What industry are you in?')}
                    />
                );
            case 3:
                return (
                    <Title
                        title={__('The obvious (but important) question…')}
                        subTitle={__('What name do you want in your logo?')}
                    />
                );
            case 4:
                return (
                    <Title
                        title={__('Help designers learn more…')}
                        subTitle={__('What type of logo are you after?')}
                    />
                );
            case 5:
                return (
                    <Title
                        title={__('Build your brand.')}
                        subTitle={__('Need more than a logo? Bundle and save.')}
                    />
                );
            default:
                return null;
        }
    },
    StepComponent: ({
        categoryKey,
        logoDesignBundlePrices,
        currentState,
        onSave,
        step,
        hasAnimation,
        resetAnimation,
    }: StepProps) => {
        const currentLogoTypes = currentState.logoTypes ? currentState.logoTypes : [];
        const currentBundleOption = currentState.bundle ? currentState.bundle : 'logo-design';

        const bundles: BundleOption[] = logoDesignBundlePrices.map(
            (bundlePrice: BundlePrice): BundleOption => ({
                ...bundlePrice,
                ...(bundlesContent.find(
                    (bundleContent: BundleContent) => bundleContent.key === bundlePrice.key
                ) as BundleContent),
            })
        );

        switch (step) {
            case 1:
                return (
                    <IntentSelect
                        onProceed={() => {
                            trackCompletedStep(currentState, categoryKey);
                            onSave({
                                ...currentState,
                                step: step + 1,
                            });
                        }}
                        category={categoryKey}
                        hasAnimation={hasAnimation}
                    />
                );
            case 2:
                return (
                    <IndustrySelect
                        industries={industries}
                        currentValue={currentState.industry}
                        onChange={key => {
                            trackEvent(COMPLETED_STEP_EVENT, {
                                step_number: step,
                                step_name: 'business industry',
                                category: categoryKey,
                                value: key,
                            });

                            onSave({
                                ...currentState,
                                industry: key,
                                step: step + 1,
                            });
                        }}
                        hasAnimation={hasAnimation}
                    />
                );
            case 3:
                return (
                    <NameInput
                        currentValue={currentState.name}
                        onChange={name => onSave({ ...currentState, name })}
                        onSubmit={() => {
                            trackCompletedStep(currentState, categoryKey);
                            onSave({
                                ...currentState,
                                step: step + 1,
                            });
                        }}
                        hasAnimation={hasAnimation}
                    />
                );
            case 4:
                return (
                    <LogoTypesSelect
                        types={logoTypes as LogoType[]}
                        currentValue={currentLogoTypes}
                        onSelect={(type: LogoTypeKey) =>
                            onSave({
                                ...currentState,
                                logoTypes: currentLogoTypes.concat([type]),
                            })
                        }
                        onDeSelect={(type: LogoTypeKey) =>
                            onSave({
                                ...currentState,
                                logoTypes: currentLogoTypes.filter(t => t !== type),
                            })
                        }
                        hasAnimation={hasAnimation}
                    />
                );
            case 5:
                return (
                    <BundleSelect
                        bundleOptions={bundles}
                        currentValue={currentBundleOption}
                        onSelect={(bundle: BundleOptionKey) =>
                            onSave({
                                ...currentState,
                                bundle,
                                step,
                            })
                        }
                        hasAnimation={hasAnimation}
                        resetAnimation={resetAnimation}
                    />
                );
            default:
                return null;
        }
    },
};

export const trackCompletedStep = (state: AppState, categoryKey: string) => {
    const { step, name, industry, logoTypes, bundle } = state;

    switch (step) {
        case 1:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'intent',
                category: categoryKey,
            });
            break;
        case 2:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'business industry',
                category: categoryKey,
                value: industry,
            });
            break;
        case 3:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'business name',
                category: categoryKey,
                value: name,
            });
            break;
        case 4:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'logo types',
                category: categoryKey,
                value: logoTypes ? logoTypes.join(',') : null,
            });
            break;
        case 5:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'bundle',
                category: categoryKey,
                value: bundle || null,
            });
            break;
    }
};
